import React, { useEffect, useState } from 'react'
import { type RenderedSectionsRef, type ShouldRenderSectionFn, type ISection } from '../SurveyFeatureStepper/types'
import { InstructorAlert, SectionWrapper } from '../../styles'
import { blue, grey } from '../../../../colors'
import { motion } from 'motion/react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Typography } from '@mui/material'
import { notification } from '../../../../components/Notifications'

interface RenderSectionProps {
  section: ISection
  root: ISection
  isRoot?: boolean
  renderedSectionsRef: RenderedSectionsRef
  renderQuestions: (section: ISection) => JSX.Element[]
  shouldRenderSection: ShouldRenderSectionFn
  triggerPulse: boolean
}

export const RenderSection = (props: RenderSectionProps): JSX.Element => {
  const { root, renderedSectionsRef, renderQuestions, shouldRenderSection, triggerPulse } = props
  const { isRoot = false, section, ...rest } = props
  const [isNewSection, setIsNewSection] = useState(false)

  useEffect(() => {
    renderedSectionsRef.current.set(section.surveySectionId, true)
    if (triggerPulse && !isRoot) {
      setIsNewSection(true)
    }

    return () => {
      renderedSectionsRef.current.delete(section.surveySectionId)
    }
  }, [section.surveySectionId, renderedSectionsRef])

  const handleSectionInteraction = (): void => {
    setIsNewSection(false)
  }

  useEffect(() => {
    if (isNewSection && triggerPulse) {
      notification('New Section Added', { variant: 'success', subTitle: section.title ?? '' })
    }
  }, [isNewSection])

  const getDisplayTitle = (): string => {
    const displayTitle = section?.title?.trim()
    return displayTitle || section?.name || ''
  }

  const getSubtitle = (): string => {
    const description = section?.descr?.trim()
    return description || ''
  }

  return (
    <React.Fragment key={section.surveySectionId}>
      {section.instructorName && isRoot && (
        <InstructorAlert severity="info" icon={<InfoOutlinedIcon sx={{ color: blue[500] }} />}>
          <Typography component="span" display="inline" fontWeight={500}>The following section should be answered for {' '}</Typography>
          <Typography component="span" display="inline" fontWeight={600}>
            {section.instructorName}
          </Typography>.
        </InstructorAlert>
      )}
      <motion.div
        initial={{ scale: isRoot ? 0.92 : 1 }}
        animate={{ scale: isRoot ? 1 : 1 }}
        // Apply the pulsing animation if this is a new section
        transition={{ duration: 0.5 }}
        style={{
          animation: isNewSection ? 'pulse 1s infinite' : 'none',
          marginTop: '4px'
        }}
        onClick={handleSectionInteraction}
      >
        <SectionWrapper>
          <Typography variant="h5" fontWeight={600} color={grey[250]}>
            {getDisplayTitle()}
          </Typography>
          {getSubtitle() && (
            <Typography fontSize={'13px'} fontWeight={400} color={blue[1100]}>
              {getSubtitle()}
            </Typography>
          )}
          {renderQuestions(section)}
        </SectionWrapper>
        {section.children.map((childSection, key) =>
          shouldRenderSection(root, childSection) &&
          <RenderSection key={key} section={childSection} {...rest} />
        )}
      </motion.div>
    </React.Fragment>
  )
}
